// ── In-page deep links ────────────────────────────────────────────────────────
// The record side of "take me there" is `recordFocus` (an id, a row, a flash).
// This is the other half: a *destination* — a tab, or a named panel on a page —
// which has no id, no row and no document behind it. "Stock Pick Suggestions" is
// a card two thirds of the way down the Tools tab of Cold Store Outflow; there is
// nothing to look up, only somewhere to be taken.
//
// A destination is addressed by the text it prints on screen (`goto=`), plus the
// tab it is under when the page knows one (`gotoTab=`). Matching on the rendered
// text is deliberate: it needs no per-widget anchor to be added and kept in step
// with a catalogue, so every card in the app is reachable the day it is written.
// The cost is that renaming a heading breaks the link — which is exactly why the
// catalogue is generated from the same source text rather than hand-maintained.
//
// The hunt retries, because the page's data has not arrived on the first look, and
// it sweeps the tab strip when the target is not under the tab that is showing —
// a widget's own tab is not recorded anywhere, and clicking through to find it is
// what an operator would do.

import { flashElement, flashSection } from './recordFocus';

export const GOTO_PARAM = 'goto';
export const GOTO_TAB_PARAM = 'gotoTab';

const HUNT_MS = 9000;
const HUNT_INTERVAL_MS = 120;
// Look under the tab that is already showing first. Sweeping immediately would
// yank the operator through three tabs before the page they landed on had even
// rendered its own content.
const TAB_SWEEP_AFTER_MS = 900;

// Headings carry live counts and separators the catalogue cannot know about
// ("Photo Tracking (4)", "Leave Balances · FY 2026"), so the comparison drops
// punctuation and digits-in-brackets rather than demanding an exact string.
function normalise(text) {
  return String(text || '')
    .replace(/\((?:\s*\d[\d,.\s/-]*)\)/g, ' ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .toLowerCase();
}

const CANDIDATE_SELECTOR = [
  '[data-card-title]',
  '[data-focus-section]',
  'h1', 'h2', 'h3', 'h4',
  '[role="tab"]',
].join(',');

/** The block to highlight for a heading — its card, section or panel, so the
 *  operator sees the thing itself and not one line of its title. */
function panelFor(el) {
  return el.closest('[data-card], [data-focus-section], section, article') || el;
}

function findByText(target) {
  const wanted = normalise(target);
  if (!wanted) return null;
  const nodes = [...document.querySelectorAll(CANDIDATE_SELECTOR)];
  const visible = nodes.filter((n) => n.offsetParent !== null || n.getClientRects().length);
  const exact = visible.find((n) => normalise(n.textContent) === wanted);
  if (exact) return exact;
  // A heading that starts with what was catalogued is the same heading with a
  // suffix the generator could not see. Anything looser than that starts matching
  // body copy, which lands the operator in the wrong half of the page.
  return visible.find((n) => normalise(n.textContent).startsWith(`${wanted} `)) || null;
}

function tabTriggers() {
  return [...document.querySelectorAll('[role="tab"]')]
    .filter((t) => t.offsetParent !== null || t.getClientRects().length);
}

/** Activate a Radix tab by its `value`. Radix does not put the value in the DOM,
 *  but it does build the trigger id from it (`radix-:r3:-trigger-tools`), and the
 *  label is the operator-facing fallback. */
function activateTab(value) {
  if (!value) return false;
  const triggers = tabTriggers();
  const wanted = String(value);
  const byId = triggers.find((t) => (t.id || '').endsWith(`-trigger-${wanted}`));
  const byText = triggers.find((t) => normalise(t.textContent) === normalise(wanted));
  const trigger = byId || byText;
  if (!trigger) return false;
  if (trigger.getAttribute('data-state') !== 'active') trigger.click();
  return true;
}

let hunt = null;   // { timer, path, token }

function stop() {
  if (hunt?.timer) window.clearInterval(hunt.timer);
  hunt = null;
}

/**
 * Reveal a destination on the page that is now mounted.
 *
 * @param {object}   req
 * @param {string}   req.find     the on-screen text to scroll to and flash
 * @param {string}   [req.tab]    Radix tab value to activate on arrival
 * @param {Function} [req.onMiss] called with `find` when the hunt gives up —
 *                                silence is the worst answer, the same reason
 *                                `recordFocus` reports its misses out loud.
 */
export function goToDestination({ find, tab, onMiss } = {}) {
  if (!find && !tab) return;
  stop();

  const startedAt = Date.now();
  const startPath = window.location.pathname;
  const sweptTabs = new Set();
  let tabRequested = false;

  const settle = (el) => {
    stop();
    // A tab trigger is a control, not a panel: flash it as a row so the pulse sits
    // on the tab itself rather than outlining the whole strip.
    if (el.getAttribute('role') === 'tab') flashElement(el);
    else flashSection(panelFor(el));
  };

  const look = () => {
    // Left the page mid-hunt: the request belonged to the previous screen, so drop
    // it silently rather than complaining about the screen they are on now.
    if (window.location.pathname !== startPath) return stop();

    if (tab && !tabRequested) tabRequested = activateTab(tab);

    if (!find) {
      // A tab with no panel named: the tab itself is the destination.
      if (tabRequested) {
        const trigger = tabTriggers().find((t) => (t.id || '').endsWith(`-trigger-${tab}`));
        if (trigger) return settle(trigger);
      }
    } else {
      const el = findByText(find);
      if (el) return settle(el);
    }

    const waited = Date.now() - startedAt;
    // Not under the tab we are looking at. A widget's tab is not recorded anywhere
    // — the catalogue knows the page and the heading — so click through the strip
    // the way an operator would, one tab per pass so each gets a chance to render.
    if (find && waited > TAB_SWEEP_AFTER_MS) {
      const next = tabTriggers().find((t) => !sweptTabs.has(t.id || t.textContent)
        && t.getAttribute('data-state') !== 'active');
      if (next) {
        sweptTabs.add(next.id || next.textContent);
        next.click();
        return undefined;
      }
    }

    if (waited > HUNT_MS) {
      stop();
      try {
        onMiss?.(find || tab);
      } catch {
        /* a bad reporter must not outlive the hunt */
      }
    }
    return undefined;
  };

  hunt = { timer: null, path: startPath };
  look();
  if (hunt) hunt.timer = window.setInterval(look, HUNT_INTERVAL_MS);
}

/** The URL that opens a catalogue destination: the page, the tab it lives under,
 *  and the heading to reveal once the page has data. */
export function destinationPath(dest) {
  if (!dest?.path) return '/';
  const params = new URLSearchParams();
  // Two kinds of tab. A page that reads its tab off the URL (`?tab=ledger`) is
  // told directly — clicking its strip would fight the router. A Radix strip has
  // no URL of its own, so it is clicked once the page is up.
  if (dest.tab) params.set(dest.tabParam || GOTO_TAB_PARAM, dest.tab);
  if (dest.find || dest.name) params.set(GOTO_PARAM, dest.find || dest.name);
  const qs = params.toString();
  return qs ? `${dest.path}${dest.path.includes('?') ? '&' : '?'}${qs}` : dest.path;
}

export default goToDestination;
